
import React, { useState } from 'react';
import { 
  ShieldCheck, 
  X, 
  Lock, 
  Globe, 
  FileText, 
  Database, 
  UserCheck, 
  Clock, 
  Server, 
  Scale,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Plus,
  Minus,
  Mail,
  Phone,
  MapPin,
  MessageSquare,
  Send
} from 'lucide-react';
import { LegalPageType } from '../types';

interface InfoPageProps {
  type: LegalPageType;
  onClose: () => void;
}

const ModalLayout: React.FC<{ title: string; subtitle: string; icon: React.ReactNode; children: React.ReactNode; onClose: () => void }> = ({ title, subtitle, icon, children, onClose }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10 animate-fade-in">
    {/* Backdrop */}
    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
    
    {/* Modal Card */}
    <div className="relative w-full max-w-4xl bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-slate-200 flex flex-col max-h-[90vh] animate-slide-up">
      {/* Header */}
      <nav className="shrink-0 bg-white/80 backdrop-blur-md border-b border-slate-100 px-8 py-6 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-600 rounded-xl text-white shadow-lg shadow-indigo-100">{icon}</div>
          <div>
            <h2 className="text-xl font-black text-slate-900 tracking-tight">{title}</h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{subtitle}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-3 bg-slate-50 text-slate-400 hover:text-rose-600 rounded-2xl transition-all">
          <X size={24} strokeWidth={2.5} />
        </button>
      </nav>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-8 md:p-12">
        <div className="max-w-none text-slate-700">
          {children}
        </div>
      </div>

      {/* Simple Footer */}
      <div className="shrink-0 p-6 bg-slate-50 border-t border-slate-100 flex justify-center">
         <button onClick={onClose} className="px-8 py-3 bg-slate-900 text-white font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-indigo-600 transition-all shadow-lg">Close Document</button>
      </div>
    </div>
  </div>
);

const FAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-center justify-between text-left group transition-all"
      >
        <span className={`text-base font-bold tracking-tight transition-colors ${isOpen ? 'text-indigo-600' : 'text-slate-900 group-hover:text-indigo-600'}`}>
          {question}
        </span>
        <div className={`p-1.5 rounded-lg transition-all ${isOpen ? 'bg-indigo-600 text-white' : 'bg-slate-50 text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600'}`}>
          {isOpen ? <Minus size={16} /> : <Plus size={16} />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] pb-5 opacity-100' : 'max-h-0 opacity-0'}`}>
        <p className="text-slate-500 text-sm leading-relaxed font-medium">{answer}</p>
      </div>
    </div>
  );
};

export const InfoPage: React.FC<InfoPageProps> = ({ type, onClose }) => {
  if (type === 'features') {
    return (
      <ModalLayout title="Platform Capabilities" subtitle="Heritage Vault Mechanics" icon={<Database size={20}/>} onClose={onClose}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3 text-slate-900"><Globe className="text-indigo-600" /> Unified Asset Mapping</h3>
              <p className="text-slate-600 text-base leading-relaxed">Connect your HDFC, ICICI, SBI, and Zerodha accounts in minutes. Our platform aggregates your global holdings into a single, high-fidelity dashboard for clear estate visualization.</p>
            </div>
            <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3 text-slate-900"><ShieldCheck className="text-emerald-600" /> Distribution Guard</h3>
              <p className="text-slate-600 text-base leading-relaxed">Never leave a gap. Our logic-engine validates every asset to ensure 100% nominee coverage. If a primary nominee is a minor, we mandate a verified guardian entry automatically.</p>
            </div>
          </div>
          <div className="space-y-6">
            <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3 text-slate-900"><Clock className="text-amber-600" /> Succession Timeline</h3>
              <p className="text-slate-600 text-base leading-relaxed">Generate automated claim checklists. Nominees receive tailored guides for physical visits, online portals, or hybrid claim processes specific to each institution's requirements.</p>
            </div>
            <div className="p-8 bg-indigo-900 rounded-[2.5rem] text-white">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3"><Database className="text-indigo-400" /> Smart Archive</h3>
              <p className="text-indigo-100/80 text-base leading-relaxed">Securely store encrypted PDFs of death certificates and succession documents to be released only to verified beneficiaries upon trigger events.</p>
            </div>
          </div>
        </div>
      </ModalLayout>
    );
  }

  if (type === 'security') {
    return (
      <ModalLayout title="Security Architecture" subtitle="Institutional-Grade Protection" icon={<Lock size={20}/>} onClose={onClose}>
        <div className="space-y-12">
          <div className="bg-slate-900 rounded-[3rem] p-10 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/20 blur-[100px] rounded-full"></div>
            <div className="relative z-10 flex flex-col md:flex-row gap-10 items-center">
              <div className="w-24 h-24 bg-white/10 rounded-3xl flex items-center justify-center shrink-0">
                <Lock size={48} className="text-indigo-400" />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-4 text-indigo-100">AES-256 Bit Encryption</h3>
                <p className="text-slate-400 leading-relaxed">Your data is encrypted at rest and in transit using 256-bit AES encryption. Even in the event of a breach, your vault contents remain unreadable without your unique cryptographic key.</p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 border border-slate-100 rounded-3xl hover:border-indigo-100 transition-all">
              <Server className="text-indigo-600 mb-4" />
              <h4 className="text-lg font-bold mb-2 text-slate-900">Tier-IV Data Centers</h4>
              <p className="text-slate-500 text-sm">Hosted on isolated instances with 99.99% uptime and physical security protocols, ensuring your heritage is always accessible when needed.</p>
            </div>
            <div className="p-8 border border-slate-100 rounded-3xl hover:border-indigo-100 transition-all">
              <UserCheck className="text-indigo-600 mb-4" />
              <h4 className="text-lg font-bold mb-2 text-slate-900">Zero-Knowledge Architecture</h4>
              <p className="text-slate-500 text-sm">We do not have access to your passwords or cleartext legacy documents. Only you and your verified nominees can unlock the specific heritage details.</p>
            </div>
          </div>
        </div>
      </ModalLayout>
    );
  }

  if (type === 'compliance') {
    return (
      <ModalLayout title="Compliance Framework" subtitle="Regulatory Boundary" icon={<Scale size={20}/>} onClose={onClose}>
        <div className="space-y-8 text-slate-600 leading-relaxed">
          <section>
            <p>NomineeDesk is built with a compliance-first philosophy and a clear operational boundary. The platform is designed strictly as an information visibility and guidance system and does not engage in regulated financial activities.</p>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Regulatory Scope</h3>
            <p>NomineeDesk is not a bank, payment system, wallet, investment intermediary, or advisory service. It does not fall under the scope of RBI, SEBI, IRDAI, or similar financial regulatory frameworks, as it does not handle money, process transactions, or provide investment or insurance advice.</p>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Foundational Security</h3>
            <p>Data security is a foundational principle of the platform. Information stored within NomineeDesk is protected through encryption, access controls, and secure authentication mechanisms. Access is role-based, ensuring that beneficiaries can only view information explicitly shared with them and cannot alter or misuse data.</p>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Read-Only Design</h3>
            <p>NomineeDesk follows a strict read-only design approach to prevent misuse or unauthorized financial activity. In the event of a security incident or suspected unauthorized access, appropriate steps are taken to assess, contain, and mitigate risks in line with industry best practices.</p>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Operational Transparency</h3>
            <p>By maintaining clear separation between information visibility and financial execution, NomineeDesk aims to provide families with clarity and peace of mind while remaining compliant, transparent, and responsibly designed.</p>
          </section>
        </div>
      </ModalLayout>
    );
  }

  if (type === 'privacy') {
    return (
      <ModalLayout title="Privacy Protocol" subtitle="Data Safeguarding" icon={<ShieldCheck size={20}/>} onClose={onClose}>
        <div className="space-y-8 text-slate-600 leading-relaxed">
          <section>
            <p>NomineeDesk respects your privacy and is committed to safeguarding the personal information you choose to share with us. Our platform is designed to help individuals and families organize and view nominee-related information in a clear and structured manner, without providing access to financial accounts or transactions.</p>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Information Collection</h3>
            <p>When you use NomineeDesk, we may collect basic personal details such as your name, email address, and contact information, along with nominee-related information that you voluntarily provide. This may include asset categories, nominee details, and allocation references. <strong>NomineeDesk does not collect bank login credentials, passwords, PINs, OTPs, account balances, or transaction data at any point.</strong></p>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Use of Information</h3>
            <p>All information collected is used solely for the purpose of providing a secure, read-only visibility platform for asset owners and their nominated beneficiaries. We do not sell, rent, or commercially share personal information with third parties. Limited data may be processed by trusted infrastructure or service providers strictly for platform operation, authentication, or security, and always under confidentiality obligations.</p>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Data Security</h3>
            <p>Reasonable administrative, technical, and organizational safeguards are implemented to protect stored data. While no digital system can guarantee absolute security, NomineeDesk follows industry-standard practices to reduce risk and unauthorized access.</p>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-900 mb-3">User Control & Acceptance</h3>
            <p>Users may update, modify, or remove their information at any time through the platform. Requests for account deletion will be honored subject to security, legal, and operational requirements. Continued use of NomineeDesk indicates acceptance of this privacy policy, which may be updated periodically to reflect changes in practices or regulations.</p>
          </section>
        </div>
      </ModalLayout>
    );
  }

  if (type === 'faq') {
    return (
      <ModalLayout title="Frequently Asked Questions" subtitle="Instant Support" icon={<HelpCircle size={20}/>} onClose={onClose}>
        <div className="space-y-2">
          <FAQItem 
            question="What is NomineeDesk?" 
            answer="NomineeDesk is a secure platform that helps you organize all your nominee and asset information in one place — so your family knows what exists and how to claim it when needed."
          />
          <FAQItem 
            question="Do you store my money or balances?" 
            answer="No. NomineeDesk never stores balances, transactions, or money. We only store nominee details, asset references, and claim guidance."
          />
          <FAQItem 
            question="What does my nominee actually see?" 
            answer="Nominees see a calm, read-only dashboard showing what assets exist, who the institution is, and how to proceed — without any sensitive financial access."
          />
          <FAQItem 
            question="Is this legally replacing bank nominations or a will?" 
            answer="No. NomineeDesk does not replace bank nominations or wills. It complements them by ensuring information clarity and visibility for your family."
          />
          <FAQItem 
            question="Is my data safe?" 
            answer="Yes. Your data is encrypted, access-controlled, and designed with privacy-first principles. Only the people you authorize can view information."
          />
        </div>
      </ModalLayout>
    );
  }

  if (type === 'contact') {
    return (
      <ModalLayout title="Contact Support" subtitle="Direct Access Channel" icon={<MessageSquare size={20}/>} onClose={onClose}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Contact Details */}
          <div className="space-y-10">
            <div>
               <h3 className="text-3xl font-black text-slate-900 tracking-tight mb-4">Get in Touch.</h3>
               <p className="text-slate-500 font-medium leading-relaxed">Our specialized heritage support team is available to assist with platform navigation, institutional sync issues, or general legacy planning queries.</p>
            </div>

            <div className="space-y-6">
               <div className="flex items-start gap-5 p-6 bg-slate-50 rounded-3xl border border-slate-100 transition-all hover:border-indigo-100">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm shrink-0"><Mail size={24} /></div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">E-Mail</div>
                    <div className="text-base font-bold text-slate-900">noineedesk@gmail.com</div>
                  </div>
               </div>

               <div className="flex items-start gap-5 p-6 bg-slate-50 rounded-3xl border border-slate-100 transition-all hover:border-indigo-100">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm shrink-0"><Phone size={24} /></div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Priority Hotline</div>
                    <div className="text-base font-bold text-slate-900">+91 (999) 555-0199</div>
                    <div className="text-[10px] text-slate-400 mt-1 font-bold uppercase">Mon-Fri, 9AM - 6PM IST</div>
                  </div>
               </div>

               <div className="flex items-start gap-5 p-6 bg-slate-50 rounded-3xl border border-slate-100 transition-all hover:border-indigo-100">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm shrink-0"><MapPin size={24} /></div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Global Headquarters</div>
                    <div className="text-base font-bold text-slate-900">TBD</div>
                    <div className="text-sm text-slate-500 font-medium">Hyderabad, TS 503001, India</div>
                  </div>
               </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-white rounded-[3rem] p-8 md:p-10 border border-slate-100 shadow-sm relative overflow-hidden group">
             <div className="absolute top-0 right-0 p-8 text-slate-50 opacity-50 transition-opacity group-hover:opacity-100"><Send size={80} /></div>
             <div className="relative z-10">
                <h4 className="text-xl font-black text-slate-900 mb-8">Send an Inquiry</h4>
                <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); alert("Inquiry transmitted securely."); onClose(); }}>
                   <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Full Identity</label>
                      <input type="text" required placeholder="Legal Name" className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold transition-all" />
                   </div>
                   <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Secure Email</label>
                      <input type="email" required placeholder="name@example.com" className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold transition-all" />
                   </div>
                   <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Message Detail</label>
                      <textarea rows={4} required placeholder="How can we assist your legacy planning today?" className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold transition-all resize-none"></textarea>
                   </div>
                   <button type="submit" className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.3em] shadow-xl shadow-indigo-100 hover:bg-indigo-700 hover:-translate-y-1 transition-all active:scale-95 flex items-center justify-center gap-3">
                      <Send size={18} /> Send
                   </button>
                </form>
             </div>
          </div>
        </div>
      </ModalLayout>
    );
  }

  return (
    <ModalLayout title="Terms & Conditions" subtitle="Service Agreement" icon={<FileText size={20}/>} onClose={onClose}>
      <div className="space-y-8 text-slate-600 leading-relaxed">
        <section>
          <p>NomineeDesk is a digital information platform intended to provide clarity and structure around nominee-related data. By accessing or using the platform, you acknowledge and agree to these terms and conditions.</p>
        </section>
        <section>
          <h3 className="text-xl font-bold text-slate-900 mb-3">Scope of Service</h3>
          <p>NomineeDesk does not function as a bank, financial institution, investment advisor, legal service provider, or asset custodian. The platform does not hold funds, execute transactions, verify claims, or provide legal, financial, or tax advice. All information displayed on NomineeDesk is informational in nature and is based on data entered by users themselves.</p>
        </section>
        <section>
          <h3 className="text-xl font-bold text-slate-900 mb-3">Owner Responsibilities</h3>
          <p>Asset owners are solely responsible for ensuring the accuracy, completeness, and relevance of the information they provide, including nominee details and allocation references. NomineeDesk does not independently verify the correctness of such information and is not responsible for outcomes resulting from inaccurate, outdated, or incomplete data.</p>
        </section>
        <section>
          <h3 className="text-xl font-bold text-slate-900 mb-3">Beneficiary Access</h3>
          <p>Beneficiaries who are granted access to NomineeDesk receive read-only visibility. They cannot modify information, initiate actions, or claim entitlement through the platform. NomineeDesk does not adjudicate disputes, validate beneficiary rights, or intervene in family, legal, or financial matters.</p>
        </section>
        <section>
          <h3 className="text-xl font-bold text-slate-900 mb-3">Liability & Risk</h3>
          <p>Use of NomineeDesk is at the user’s discretion and risk. The platform shall not be liable for financial losses, delayed claims, disputes, or decisions taken based on the information presented. NomineeDesk reserves the right to suspend or terminate access in cases of misuse, security threats, or violation of these terms.</p>
        </section>
      </div>
    </ModalLayout>
  );
};
